import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Container, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { TextDecrypt } from './TextDecrypt';
import Resume from '../../settings/resume.json';
import { FirstName, LastName } from '../../utils/getName';
import devDark from '../../assets/images/Dev-d2.webp';
import devLight from '../../assets/images/Dev-l2.webp';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  main: {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  heading: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
      marginRight: theme.spacing(6),
    },
  },
  image: {
    width: '100%',
    maxWidth: 400,
    height: 'auto',
    objectFit: 'contain',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      marginBottom: 0,
      marginLeft: theme.spacing(6),
    },
  },
  name: {
    fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
    textShadow: theme.palette.mode === 'dark' 
      ? '0 0 10px rgba(0, 255, 255, 0.7)'
      : '0 0 10px rgba(0, 0, 255, 0.5)',
    marginBottom: theme.spacing(2),
  },
  jobs: {
    fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
    fontStyle: 'italic',
    marginBottom: theme.spacing(2),
  },
  typedText: {
    fontFamily: "'Courier New', monospace",
    fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
    letterSpacing: 1,
    color: theme.palette.text.secondary,
  },
}));

const TEXTS = ['Software Engineering', 'Web Development', 'Cloud Enthusiast', 'Blogging', 'UI Designing'];
const TYPING_SPEED = 100;
const PAUSE_DURATION = 1500;

export const Content = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const imageSrc = theme.palette.mode === 'dark' ? devDark : devLight;

  const typeCurrentWord = useCallback(() => {
    const currentWord = TEXTS[index];
    let charIndex = 0;

    const typing = setInterval(() => {
      setTypedText((prev) => prev + currentWord[charIndex]);
      charIndex++;
      if (charIndex === currentWord.length) {
        clearInterval(typing);
        setIsTyping(false);
      }
    }, TYPING_SPEED);
  }, [index]);

  useEffect(() => {
    if (isTyping) {
      typeCurrentWord();
    } else {
      const timeout = setTimeout(() => {
        setTypedText('');
        setIndex((prev) => (prev + 1) % TEXTS.length);
        setIsTyping(true);
      }, PAUSE_DURATION);
      return () => clearTimeout(timeout);
    }
  }, [isTyping, typeCurrentWord]);

  return (
    <Container component="main" className={classes.main} maxWidth="lg">
      <img 
        src={imageSrc} 
        alt="Profile" 
        className={clsx(classes.image)} 
        loading="eager" 
      />
      <div className={classes.heading}>
        <Typography variant="h1" className={classes.name}>
          <TextDecrypt text={`${FirstName} ${LastName}`} />
        </Typography>
        <Typography variant="h2" className={classes.jobs}>
          <TextDecrypt text={Resume.basics.job1} />
          <TextDecrypt text={Resume.basics.job2} />
        </Typography>
        <Typography variant="h3" className={classes.typedText}>
          {typedText}
        </Typography>
      </div>
    </Container>
  );
};
